/**
 * @summary The Default Header For the Landing Pages.
 */
import React, { forwardRef, useContext } from 'react';
import PropTypes from 'prop-types';
import cx from 'utils/classnames';
import Wysiwyg from 'components/blocks/wysiwyg/Wysiwyg';
import CtaButton from 'components/blocks/ctaButton/CtaButton';
import { CtaPropTypes } from 'utils/propTypes';
import { TMS_EVENT_TYPES } from 'utils/analytics/EVENT_TYPES';
import ToolTip from 'components/base/toolTip/ToolTip';
import Logo from 'components/base/logo/Logo';
import GlobalContext from 'hooks/contexts/GlobalContext';
import MainNav from 'components/sections/mainNav/MainNav';

function DefaultLandingHeader({
  cta,
  headertext,
  siteName,
  className = 'header--landingPage',
  menuItems,
  innerRef,
  logoLink,
}) {
  const headerClass = cx({
    header: true,
    'header--hasHeaderText': !!headertext,
    [className]: true,
  });

  const {
    siteMeta: { disclosure },
  } = useContext(GlobalContext);

  return (
    <header className={headerClass} ref={innerRef}>
      <div className="header__inner">
        <Logo siteName={siteName} logoLink={logoLink} />
        {headertext && (
          <div className="header__secondary">
            <Wysiwyg content={headertext} />
          </div>
        )}
        {menuItems?.length > 0 && (
          <nav className="header__nav" role="navigation" aria-label="Main">
            <MainNav menuItems={menuItems} />
          </nav>
        )}
        {cta && cta.useCTA && (
          <div className="header__secondary">
            <CtaButton
              buttonText={cta.buttonText}
              buttonActionType={cta.buttonActionType}
              link={cta.link}
              trackingLabel={TMS_EVENT_TYPES.Header}
            />
          </div>
        )}
        {disclosure && (
          <div className="header__disclosure">
            <ToolTip label="Advertiser Disclosure" positioning="bottom">
              <Wysiwyg isClean content={disclosure} />
            </ToolTip>
          </div>
        )}
      </div>
    </header>
  );
}

DefaultLandingHeader.propTypes = {
  headertext: PropTypes.string,
  className: PropTypes.string,
  cta: CtaPropTypes,
  siteName: PropTypes.string,
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  logoLink: PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string,
    target: PropTypes.string,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  menuItems: PropTypes.array,
};

const ForwardedDefaultLandingHeader = forwardRef((props, ref) => {
  const { cta, headertext, siteName, className, logoLink, menuItems } = props;
  return (
    <DefaultLandingHeader
      cta={cta}
      headertext={headertext}
      siteName={siteName}
      className={className}
      logoLink={logoLink}
      menuItems={menuItems}
      innerRef={ref}
    />
  );
});

ForwardedDefaultLandingHeader.displayName = 'DefaultLandingHeader';

export default ForwardedDefaultLandingHeader;
